import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { useQueryClient } from "@tanstack/react-query"
import { getGetMeQueryOptions, useGetMe, useLogout } from "@/api/generated/identity/identity"
import type { Session } from "@/api/generated/model"
import { STALE } from "@/api/query-client"
import { refreshOnce } from "./refresh"
import {
  clearLogoutPending,
  hasLogoutPending,
  markLogoutPending,
  markSessionHint,
} from "./session-hint"
import { bindSessionCache, onSessionLost } from "./session-lost"
import { tokenStore } from "./token-store"

/** Whether this tab is signed in; `pending` until the bootstrap refresh answers. */
export type SessionState = "pending" | "authenticated" | "anonymous"
/** What the bootstrap refresh found, fixed once it answers. */
export type BootResult = "pending" | "restored" | "anonymous"

export interface SessionApi {
  state: SessionState
  boot: BootResult
  /** Every sign-in ends here (LLD §6.8): the token in memory, the boot hint set. */
  signIn: (session: Session) => void
  /** `logout`, then the disruptive exit (FR-DSH-SES-04). `idle` says why on sign-in. */
  signOut: (options?: { reason?: "idle" }) => Promise<void>
}

const SessionContext = createContext<SessionApi | null>(null)
const hasToken = () => tokenStore.get() !== null

/**
 * The session (LLD §6.3). At mount it asks the refresh cookie for a session
 * once (FR-DSH-SES-01): a reload keeps nothing in memory, so the cookie is
 * the only way back. Until that answers the state is `pending`, and the
 * gates hold their screens (`RequireSession`, the auth guards).
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const signedIn = useSyncExternalStore(tokenStore.subscribe, hasToken)
  const [boot, setBoot] = useState<BootResult>(() => (hasToken() ? "restored" : "pending"))
  // Stable (TanStack keeps mutateAsync), so signOut is stable too.
  const { mutateAsync: logout } = useLogout()

  useEffect(() => bindSessionCache(queryClient), [queryClient])

  useEffect(() => {
    if (boot !== "pending") return
    let live = true
    const settle = (b: BootResult) => {
      if (live) setBoot(b)
    }
    if (hasLogoutPending()) {
      // The last sign-out never reached the server: finish it, and do not
      // let the still-live cookie restore the session it ended.
      void logout()
        .then(clearLogoutPending, () => undefined)
        .then(() => {
          settle("anonymous")
        })
    } else {
      void refreshOnce().then((ok) => {
        // The caller next, at once, not when the shell mounts and asks
        // (Task 7.2): `useMe` finds this request in flight.
        if (ok)
          void queryClient.query(getGetMeQueryOptions({ query: ME_QUERY })).catch(() => undefined)
        settle(ok ? "restored" : "anonymous")
      })
    }
    return () => {
      live = false
    }
  }, [boot, logout, queryClient])

  const signIn = useCallback((session: Session) => {
    tokenStore.set(session.accessToken)
    markSessionHint()
    clearLogoutPending()
  }, [])

  const signOut = useCallback(
    async (options?: { reason?: "idle" }) => {
      try {
        await logout()
      } catch {
        // Unreachable server: the session here still ends, and the next boot
        // sends the logout before anything else.
        markLogoutPending()
      }
      const reason = options?.reason
      onSessionLost(reason ? { hadToken: true, reason } : { hadToken: true, signedOut: true })
    },
    [logout],
  )

  const state: SessionState = signedIn
    ? "authenticated"
    : boot === "pending"
      ? "pending"
      : "anonymous"
  const api = useMemo(() => ({ state, boot, signIn, signOut }), [state, boot, signIn, signOut])
  return <SessionContext value={api}>{children}</SessionContext>
}

export function useSession(): SessionApi {
  const api = useContext(SessionContext)
  if (!api) throw new Error("useSession needs a SessionProvider above it (src/app/providers.tsx)")
  return api
}

/** The caller's query options: one cache entry, refetched only when invalidated. */
const ME_QUERY = { staleTime: STALE.session, retry: false } as const

/** The caller (LLD §6.4). */
export function useMe() {
  return useGetMe({ query: ME_QUERY })
}
